/**
 * Cliente HTTP tipado. Os tipos e as rotas vem de `@zenda/api-contract` — este
 * arquivo nao declara nenhum campo proprio.
 *
 * Fala SEMPRE com a API real. Nao ha fallback: uma falha aparece como falha.
 */
import {
  API_ROUTES,
  type Appointment,
  type Client,
  type ClientDetail,
  type ClientUpsertRequest,
  type ConnectCheck,
  type ConnectLink,
  type ConviteInfo,
  type ReviewSendRequest,
  type ReviewSendResult,
  type ReviewStatus,
  type ReviewTemplateRequest,
  type ReviewTemplateResult,
  type ClientTemplates,
  type CreateTemplateRequest,
  type ConversationTag,
  type DependentUpsert,
  type PatientCreate,
  type PatientCreated,
  type PatientDetail,
  type PatientRecordUpdate,
  type PatientSummary,
  type StartConnectionResponse,
  type ConversationDetail,
  type ConversationSummary,
  type DashboardStats,
  type MonthlyMessageCost,
  type Doctor,
  type LoginRequest,
  type LoginResponse,
  type Me,
  type Message,
  type MetaOnboardRequest,
  type MetaOnboardResult,
  type MetaSessionEvent,
  type MetaSignupConfig,
  type SendMessageRequest,
  type Uuid,
  type TemplateModel,
  type TemplateModelUpsert,
  type QuickReply,
  type QuickReplyUpsert,
  type AgendaConfig,
  type AgendaDay,
  type AgendaIntegration,
  type CalendarLinkUpsert,
  type PlatformDiscovery,
  type PlatformDiscoveryRequest,
  type SyncResult,
  type AgendaSettings,
  type AppointmentTypeUpsert,
  type AvailabilityBulkRequest,
  type AvailabilityDay,
  type AvailabilityExceptionUpsert,
  type AvailabilityRuleUpsert,
  type LocationUpsert,
  type BookRequest,
  type BookResult,
  type FreeSlotSearch,
  type SlotCheckResult,
  type SendTemplateRequest,
  type PatientAgenda,
  type ContactHit,
  type PresenceRequest,
  type RescheduleRequest,
  type TrocarSenhaRequest,
  type AcceptInviteRequest,
  type AttestationText,
  type CreateTeamRequest,
  type DeclineInviteRequest,
  type IncomingInvite,
  type InboxFocus,
  type MeuPlantao,
  type PerfilUpdate,
  type ShareRequest,
  type SharePolicyRequest,
  type TeamDetail,
  type TeamInviteRequest,
  type TeamSummary,
  type TeamTerm,
  type UpdateMemberRequest,
  type UpdateShareRequest,
  type ActivityEntry,
  type AssignRequest,
  type ShiftOverrideRequest,
  type ShiftRuleRequest,
  type TeamShifts,
  PUBLIC_ROUTES,
} from '@zenda/api-contract';
import { dict } from './dict';

const TOKEN_KEY = 'zenda.token';

export class ApiRequestError extends Error {
  readonly code: string;
  readonly requestId: string | null;
  readonly status: number | null;

  constructor(
    code: string,
    message: string,
    requestId: string | null = null,
    status: number | null = null,
  ) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = code;
    this.requestId = requestId;
    this.status = status;
  }
}

export function novoRequestId(): string {
  return typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** O começo do id basta para achar a requisição no painel, e cabe numa frase. */
export const codigoCurto = (requestId: string): string => requestId.slice(0, 8);

export function getToken(): string | null {
  return window.localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  window.localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  window.localStorage.removeItem(TOKEN_KEY);
}

/**
 * O token guardado ja passou do prazo que ele mesmo declara.
 *
 * Ler o `exp` aqui NAO e confiar no token: a assinatura so o servidor confere, e ele
 * confere em toda chamada. Mas um token que se declara vencido esta vencido de
 * qualquer jeito — mexer no campo para dizer o contrario derruba o HMAC —, e isso
 * basta para o app nao subir "logado" para depois piscar de volta ao login no primeiro
 * 401. E leitura local, sem ida ao servidor, antes da primeira pintura.
 *
 * `true` tambem quando o formato nao e o esperado: token que nao se le nao vai
 * autenticar nada.
 */
export function tokenVencido(): boolean {
  const token = getToken();
  if (token === null) return true;
  const payload = token.split('.')[0];
  if (payload === undefined || payload === '') return true;
  try {
    const { exp } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      exp?: number;
    };
    return typeof exp !== 'number' || exp <= Date.now();
  } catch {
    return true;
  }
}

/**
 * Existia aqui um servidor de demonstracao em memoria, que assumia quando a API falhava.
 * Foi REMOVIDO: com a API real no ar, ele so serviria para transformar uma falha numa
 * tela cheia de dados plausiveis e falsos — e quem estivesse avaliando o produto nao
 * teria como saber. Falhar visivelmente e o comportamento correto.
 */

function unauthorized(): never {
  clearToken();
  if (window.location.pathname !== '/login') {
    window.location.assign('/login');
  }
  throw new ApiRequestError('unauthorized', 'Sessao expirada. Entre novamente.');
}

interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly body?: unknown;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const token = getToken();
  const requestId = novoRequestId();
  const headers: Record<string, string> = { Accept: 'application/json', 'X-Request-Id': requestId };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (token !== null) headers['Authorization'] = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    });
  } catch {
    throw new ApiRequestError('network_error', dict().common.networkError, requestId);
  }

  if (response.status === 401) {
    // No login, 401 e "credenciais erradas" — nao ha sessao para expirar.
    if (path === API_ROUTES.login) {
      throw new ApiRequestError('invalid_credentials', 'E-mail ou senha invalidos.');
    }
    unauthorized();
  }

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      code?: string;
      message?: string;
    } | null;
    const id = response.headers.get('x-request-id') ?? requestId;
    const base = payload?.message ?? `Falha na requisicao (${response.status}).`;
    if (payload?.code === 'TROCA_DE_SENHA' && window.location.pathname !== '/trocar-senha') {
      window.location.assign('/trocar-senha');
    }
    throw new ApiRequestError(
      payload?.code ?? 'http_error',
      response.status >= 500 ? `${base} (código ${codigoCurto(id)})` : base,
      id,
      response.status,
    );
  }

  return (await response.json()) as T;
}

// ── Rotas ───────────────────────────────────────────────────────────────────

export function login(credentials: LoginRequest): Promise<LoginResponse> {
  return request<LoginResponse>(API_ROUTES.login, {
    method: 'POST',
    body: credentials,
  });
}

export const fetchMe = (): Promise<Me> => request<Me>(API_ROUTES.me);

export const trocarSenha = (body: TrocarSenhaRequest): Promise<{ token: string }> =>
  request<{ token: string }>(PUBLIC_ROUTES.trocarSenha, { method: 'POST', body });

/**
 * Os medicos e clinicas que a atendente atende. Nao ha "troca de conta": o token vale
 * para a operacao dela inteira, e o cliente e uma DIMENSAO do dado, nao um contexto
 * que se veste e se tira.
 */
export const fetchClients = (): Promise<readonly Client[]> =>
  request<readonly Client[]>(API_ROUTES.clients);

/** Um cliente inteiro: medicos, canais e o historico de cobranca dele. */
export const fetchClient = (id: Uuid): Promise<ClientDetail> =>
  request<ClientDetail>(API_ROUTES.client(id));

/**
 * Devolve so o `id`, e nao o `Client` inteiro, de proposito: um cliente recem-criado
 * tem todas as estatisticas em zero, e montar o objeto completo custaria rodar de novo
 * a query pesada de `/api/clients` para nao dizer nada. Quem precisa do resto refaz a
 * lista — que o hook ja invalida.
 */
export const createClient = (payload: ClientUpsertRequest): Promise<{ readonly id: Uuid }> =>
  request<{ readonly id: Uuid }>(API_ROUTES.clients, { method: 'POST', body: payload });

/**
 * PATCH de verdade: o servidor so mexe nos campos PRESENTES no corpo. Omitir um campo
 * o deixa como esta; mandar `null` o limpa de proposito.
 */
export const updateClient = (
  id: Uuid,
  payload: ClientUpsertRequest,
): Promise<{ readonly ok: boolean }> =>
  request<{ readonly ok: boolean }>(API_ROUTES.client(id), { method: 'PATCH', body: payload });

/**
 * Comeca a conexao do WhatsApp de um medico.
 *
 * A resposta carrega `mode`: em producao e `embedded_signup` e o fluxo e da Meta; no
 * ambiente de demonstracao e `simulated`. A UI e OBRIGADA a mostrar essa distincao —
 * demo que se disfarca de integracao e como alguem descobre, na frente de um cliente,
 * que nao funciona.
 */
export const connectChannel = (doctorId: Uuid): Promise<StartConnectionResponse> =>
  request<StartConnectionResponse>(API_ROUTES.connectChannel(doctorId), { method: 'POST' });

export const reconnectChannel = (channelId: Uuid): Promise<StartConnectionResponse> =>
  request<StartConnectionResponse>(API_ROUTES.reconnectChannel(channelId), { method: 'POST' });

export const fetchMetaConfig = (): Promise<MetaSignupConfig> =>
  request<MetaSignupConfig>(API_ROUTES.metaConfig);

/**
 * Gera o link que a secretaria manda para o medico.
 *
 * O segredo vem na resposta e NAO e recuperavel: gerar de novo mata o anterior. E o
 * mesmo contrato de um convite de verdade — quem perdeu pede outro.
 */
export const createConnectLink = (doctorId: Uuid): Promise<ConnectLink> =>
  request<ConnectLink>(API_ROUTES.connectLink(doctorId), { method: 'POST' });

export const checkConnectLink = (doctorId: Uuid): Promise<ConnectCheck> =>
  request<ConnectCheck>(API_ROUTES.connectLinkCheck(doctorId), { method: 'POST' });

export const fetchConviteGupshupLink = (token: string): Promise<{ url: string }> =>
  request<{ url: string }>(API_ROUTES.conviteGupshupLink(token), { method: 'POST' });

// ── O lado do medico: sem sessao, autenticado pelo segredo da URL ─────────────

export const fetchConvite = (token: string): Promise<ConviteInfo> =>
  request<ConviteInfo>(API_ROUTES.convite(token));

export const fetchConviteMetaConfig = (token: string): Promise<MetaSignupConfig> =>
  request<MetaSignupConfig>(API_ROUTES.conviteMetaConfig(token));

export const logConviteSession = (
  token: string,
  event: Omit<MetaSessionEvent, 'doctorId' | 'clientId'>,
): Promise<void> =>
  request<{ readonly ok: boolean }>(API_ROUTES.conviteSessions(token), {
    method: 'POST',
    body: event,
  })
    .then(() => undefined)
    .catch(() => undefined);

export const onboardPeloConvite = (
  token: string,
  payload: Omit<MetaOnboardRequest, 'doctorId'>,
): Promise<MetaOnboardResult> =>
  request<MetaOnboardResult>(API_ROUTES.conviteOnboard(token), { method: 'POST', body: payload });

/**
 * Registra o andamento do fluxo, inclusive desistencia.
 *
 * Nao propaga falha de proposito: perder uma linha de funil e barato, e deixar o
 * onboarding do medico morrer porque o log falhou seria trocar o essencial pelo
 * acessorio.
 */
export const logMetaSession = (event: MetaSessionEvent): Promise<void> =>
  request<{ readonly id: Uuid }>(API_ROUTES.metaSessions, { method: 'POST', body: event })
    .then(() => undefined)
    .catch(() => undefined);

/** O `code` vive 30 s: esta chamada sai assim que o fluxo fecha, sem passo intermediario. */
export const onboardChannel = (payload: MetaOnboardRequest): Promise<MetaOnboardResult> =>
  request<MetaOnboardResult>(API_ROUTES.metaOnboard, { method: 'POST', body: payload });

/**
 * Baixa manual de uma cobranca. E ela dizendo "recebi", nao uma conciliacao bancaria:
 * os medicos nao pagam o Zenda, quem paga e a atendente. Aqui e so o lembrete dela.
 */
export const markPaymentPaid = (clientId: Uuid, month: string): Promise<{ readonly ok: boolean }> =>
  request<{ readonly ok: boolean }>(API_ROUTES.markPaymentPaid(clientId, month), {
    method: 'POST',
  });

export const markPaymentUnpaid = (
  clientId: Uuid,
  month: string,
): Promise<{ readonly ok: boolean }> =>
  request<{ readonly ok: boolean }>(API_ROUTES.markPaymentUnpaid(clientId, month), {
    method: 'POST',
  });

export const disconnectChannel = (channelId: Uuid): Promise<{ readonly ok: boolean }> =>
  request<{ readonly ok: boolean }>(API_ROUTES.disconnectChannel(channelId), { method: 'POST' });

export const syncChannel = (channelId: Uuid): Promise<{ readonly ok: boolean }> =>
  request<{ readonly ok: boolean }>(API_ROUTES.syncChannel(channelId), { method: 'POST' });

export const fetchDoctors = (): Promise<readonly Doctor[]> =>
  request<readonly Doctor[]>(API_ROUTES.doctors);

/**
 * A lista do inbox. `todos` ignora o foco salvo: é o "ver todos os médicos"
 * (docs/domain/equipes.md §7.1).
 */
export const fetchConversations = (todos = false): Promise<readonly ConversationSummary[]> =>
  request<readonly ConversationSummary[]>(
    `${API_ROUTES.conversations}${todos ? '?escopo=todos' : ''}`,
  );

export const fetchConversation = (id: Uuid): Promise<ConversationDetail> =>
  request<ConversationDetail>(API_ROUTES.conversation(id));

export const sendMessage = (id: Uuid, payload: SendMessageRequest): Promise<Message> =>
  request<Message>(API_ROUTES.sendMessage(id), { method: 'POST', body: payload });

export const readConversation = (id: Uuid): Promise<{ readonly ok: boolean }> =>
  request<{ readonly ok: boolean }>(API_ROUTES.read(id), { method: 'POST' });

export const reopenConversation = (id: Uuid): Promise<{ readonly ok: boolean }> =>
  request<{ readonly ok: boolean }>(API_ROUTES.reopen(id), { method: 'POST' });

export const resolveConversation = (id: Uuid): Promise<ConversationSummary> =>
  request<ConversationSummary>(API_ROUTES.resolve(id), { method: 'POST' });

export const fetchAppointments = (): Promise<readonly Appointment[]> =>
  request<readonly Appointment[]>(API_ROUTES.appointments);

export const fetchStats = (): Promise<DashboardStats> => request<DashboardStats>(API_ROUTES.stats);

export const fetchMonthlyCost = (): Promise<MonthlyMessageCost> =>
  request<MonthlyMessageCost>(API_ROUTES.costsMonth);

// ── Pacientes ───────────────────────────────────────────────────────────────

export const fetchPatients = (
  clientId: Uuid,
  opts: { readonly q?: string; readonly due?: boolean } = {},
): Promise<readonly PatientSummary[]> => {
  const qs = new URLSearchParams();
  if (opts.q) qs.set('q', opts.q);
  if (opts.due) qs.set('due', 'true');
  const sufixo = qs.toString();
  return request<readonly PatientSummary[]>(
    API_ROUTES.clientPatients(clientId) + (sufixo === '' ? '' : `?${sufixo}`),
  );
};

export const fetchPatient = (clientId: Uuid, contactId: Uuid): Promise<PatientDetail> =>
  request<PatientDetail>(API_ROUTES.patient(clientId, contactId));

export const createPatient = (clientId: Uuid, payload: PatientCreate): Promise<PatientCreated> =>
  request<PatientCreated>(API_ROUTES.clientPatients(clientId), { method: 'POST', body: payload });

export const createDependent = (
  clientId: Uuid,
  contactId: Uuid,
  payload: DependentUpsert,
): Promise<{ readonly id: Uuid }> =>
  request<{ readonly id: Uuid }>(API_ROUTES.patientDependents(clientId, contactId), {
    method: 'POST',
    body: payload,
  });

export const updateDependent = (
  clientId: Uuid,
  contactId: Uuid,
  dependentId: Uuid,
  payload: DependentUpsert,
): Promise<{ readonly ok: boolean }> =>
  request<{ readonly ok: boolean }>(API_ROUTES.patientDependent(clientId, contactId, dependentId), {
    method: 'PATCH',
    body: payload,
  });

export const updatePatient = (
  clientId: Uuid,
  contactId: Uuid,
  payload: PatientRecordUpdate,
): Promise<{ readonly ok: boolean }> =>
  request<{ readonly ok: boolean }>(API_ROUTES.patient(clientId, contactId), {
    method: 'PATCH',
    body: payload,
  });

// ── Tags ────────────────────────────────────────────────────────────────────

export const fetchTags = (conversationId?: Uuid): Promise<readonly ConversationTag[]> =>
  request<readonly ConversationTag[]>(
    conversationId === undefined
      ? API_ROUTES.tags
      : `${API_ROUTES.tags}?conversationId=${conversationId}`,
  );

export const createTag = (payload: { readonly name: string; readonly color?: string }) =>
  request<{ readonly id: Uuid }>(API_ROUTES.tags, { method: 'POST', body: payload });

export const deleteTag = (id: Uuid) =>
  request<{ readonly ok: boolean }>(API_ROUTES.tag(id), { method: 'DELETE' });

/** Reabre a conversa com um template aprovado. O único caminho depois das 24 h. */
export const sendPatientTemplate = (
  clientId: Uuid,
  contactId: Uuid,
  payload: SendTemplateRequest,
): Promise<{ readonly id: Uuid; readonly conversationId: Uuid }> =>
  request<{ readonly id: Uuid; readonly conversationId: Uuid }>(
    API_ROUTES.patientTemplates(clientId, contactId),
    { method: 'POST', body: payload },
  );

export const sendConversationTemplate = (
  conversationId: Uuid,
  payload: SendTemplateRequest,
): Promise<{ readonly id: Uuid }> =>
  request<{ readonly id: Uuid }>(API_ROUTES.conversationTemplate(conversationId), {
    method: 'POST',
    body: payload,
  });

export const fetchClientTemplates = (clientId: Uuid): Promise<ClientTemplates> =>
  request<ClientTemplates>(API_ROUTES.clientTemplates(clientId));

export const createClientTemplate = (clientId: Uuid, payload: CreateTemplateRequest) =>
  request<{ readonly id: string; readonly status: string }>(API_ROUTES.clientTemplates(clientId), {
    method: 'POST',
    body: payload,
  });

/** Aceita tag existente (`tagId`) ou nome novo — criar antes de usar mata o recurso. */
export const tagConversation = (
  id: Uuid,
  payload: { readonly tagId?: Uuid; readonly name?: string; readonly color?: string },
): Promise<{ readonly id: Uuid }> =>
  request<{ readonly id: Uuid }>(API_ROUTES.conversationTags(id), {
    method: 'POST',
    body: payload,
  });

export const untagConversation = (id: Uuid, tagId: Uuid): Promise<{ readonly ok: boolean }> =>
  request<{ readonly ok: boolean }>(API_ROUTES.conversationTag(id, tagId), { method: 'DELETE' });

// ── Console de verificacao (App Review) ─────────────────────────────────────

export const fetchReviewStatus = (): Promise<ReviewStatus> =>
  request<ReviewStatus>(API_ROUTES.reviewStatus);

export const reviewSend = (payload: ReviewSendRequest): Promise<ReviewSendResult> =>
  request<ReviewSendResult>(API_ROUTES.reviewSend, { method: 'POST', body: payload });

export const reviewCreateTemplate = (
  payload: ReviewTemplateRequest,
): Promise<ReviewTemplateResult> =>
  request<ReviewTemplateResult>(API_ROUTES.reviewTemplate, { method: 'POST', body: payload });

/**
 * Anexo para o paciente.
 *
 * `FormData` cru, sem `Content-Type` no header: o navegador precisa montar o
 * boundary do multipart sozinho, e defini-lo à mão faz o servidor não achar as
 * partes. É por isso que esta chamada não passa pelo `request()`, que serializa
 * JSON.
 */
export const sendMedia = async (
  id: Uuid,
  file: File,
  caption: string,
): Promise<{ readonly id: Uuid }> => {
  const form = new FormData();
  if (caption.trim() !== '') form.append('caption', caption.trim());
  form.append('file', file);

  const token = getToken();
  const r = await fetch(`/api/conversations/${id}/media`, {
    method: 'POST',
    headers: token === null ? {} : { Authorization: `Bearer ${token}` },
    body: form,
  });

  const json: unknown = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = json as { message?: string };
    throw new Error(e.message ?? 'Não foi possível enviar o arquivo.');
  }
  return json as { readonly id: Uuid };
};

// ── Biblioteca e respostas prontas ──────────────────────────────────────────

export const fetchTemplateModels = (): Promise<readonly TemplateModel[]> =>
  request<readonly TemplateModel[]>(API_ROUTES.templateModels);

export const saveTemplateModel = (payload: TemplateModelUpsert): Promise<{ readonly id: Uuid }> =>
  request<{ readonly id: Uuid }>(API_ROUTES.templateModels, { method: 'POST', body: payload });

export const deleteTemplateModel = (id: Uuid): Promise<{ readonly ok: boolean }> =>
  request<{ readonly ok: boolean }>(API_ROUTES.templateModel(id), { method: 'DELETE' });

export const fetchQuickReplies = (): Promise<readonly QuickReply[]> =>
  request<readonly QuickReply[]>(API_ROUTES.quickReplies);

export const createQuickReply = (payload: QuickReplyUpsert): Promise<{ readonly id: Uuid }> =>
  request<{ readonly id: Uuid }>(API_ROUTES.quickReplies, { method: 'POST', body: payload });

export const updateQuickReply = (
  id: Uuid,
  payload: QuickReplyUpsert,
): Promise<{ readonly ok: boolean }> =>
  request<{ readonly ok: boolean }>(API_ROUTES.quickReply(id), { method: 'PATCH', body: payload });

export const deleteQuickReply = (id: Uuid): Promise<{ readonly ok: boolean }> =>
  request<{ readonly ok: boolean }>(API_ROUTES.quickReply(id), { method: 'DELETE' });

// ── Agenda ──────────────────────────────────────────────────────────────────

export const fetchAgenda = (clientId: Uuid): Promise<AgendaConfig> =>
  request<AgendaConfig>(API_ROUTES.agendaConfig(clientId));

export const saveAgendaSettings = (
  clientId: Uuid,
  payload: Partial<AgendaSettings>,
): Promise<AgendaSettings> =>
  request<AgendaSettings>(API_ROUTES.agendaSettings(clientId), { method: 'PATCH', body: payload });

export const createLocation = (
  clientId: Uuid,
  payload: LocationUpsert,
): Promise<{ readonly id: Uuid }> =>
  request<{ readonly id: Uuid }>(API_ROUTES.locations(clientId), { method: 'POST', body: payload });

export const updateLocation = (
  clientId: Uuid,
  id: Uuid,
  payload: LocationUpsert,
): Promise<{ readonly ok: boolean }> =>
  request<{ readonly ok: boolean }>(API_ROUTES.location(clientId, id), {
    method: 'PATCH',
    body: payload,
  });

export const deactivateLocation = (clientId: Uuid, id: Uuid): Promise<{ readonly ok: boolean }> =>
  request<{ readonly ok: boolean }>(API_ROUTES.location(clientId, id), { method: 'DELETE' });

export const saveAvailabilityBulk = (
  clientId: Uuid,
  payload: AvailabilityBulkRequest,
): Promise<{ readonly count: number }> =>
  request<{ readonly count: number }>(API_ROUTES.rulesBulk(clientId), {
    method: 'POST',
    body: payload,
  });

export const updateAvailabilityRule = (
  clientId: Uuid,
  id: Uuid,
  payload: AvailabilityRuleUpsert,
): Promise<{ readonly ok: boolean }> =>
  request<{ readonly ok: boolean }>(API_ROUTES.rule(clientId, id), {
    method: 'PATCH',
    body: payload,
  });

export const deleteAvailabilityRule = (
  clientId: Uuid,
  id: Uuid,
): Promise<{ readonly ok: boolean }> =>
  request<{ readonly ok: boolean }>(API_ROUTES.rule(clientId, id), { method: 'DELETE' });

export const createAppointmentType = (
  clientId: Uuid,
  payload: AppointmentTypeUpsert,
): Promise<{ readonly id: Uuid }> =>
  request<{ readonly id: Uuid }>(API_ROUTES.agendaTypes(clientId), {
    method: 'POST',
    body: payload,
  });

export const updateAppointmentType = (
  clientId: Uuid,
  id: Uuid,
  payload: AppointmentTypeUpsert,
): Promise<{ readonly ok: boolean }> =>
  request<{ readonly ok: boolean }>(API_ROUTES.agendaType(clientId, id), {
    method: 'PATCH',
    body: payload,
  });

export const deactivateAppointmentType = (
  clientId: Uuid,
  id: Uuid,
): Promise<{ readonly ok: boolean }> =>
  request<{ readonly ok: boolean }>(API_ROUTES.agendaType(clientId, id), { method: 'DELETE' });

export const createAvailabilityException = (
  clientId: Uuid,
  payload: AvailabilityExceptionUpsert,
): Promise<{ readonly count: number }> =>
  request<{ readonly count: number }>(API_ROUTES.exceptions(clientId), {
    method: 'POST',
    body: payload,
  });

export const deleteAvailabilityException = (
  clientId: Uuid,
  id: Uuid,
): Promise<{ readonly ok: boolean }> =>
  request<{ readonly ok: boolean }>(API_ROUTES.exception(clientId, id), { method: 'DELETE' });

export const fetchAvailabilityDays = (
  clientId: Uuid,
  from: string,
  to: string,
): Promise<readonly AvailabilityDay[]> =>
  request<readonly AvailabilityDay[]>(API_ROUTES.availabilityDays(clientId, from, to));

// ── Integração com plataforma de terceiro ───────────────────────────────────

export const fetchAgendaIntegration = (clientId: Uuid): Promise<AgendaIntegration> =>
  request<AgendaIntegration>(API_ROUTES.agendaIntegration(clientId));

export const createCalendarLink = (
  clientId: Uuid,
  payload: CalendarLinkUpsert,
): Promise<{ readonly id: Uuid }> =>
  request<{ readonly id: Uuid }>(API_ROUTES.calendarLinks(clientId), {
    method: 'POST',
    body: payload,
  });

export const deactivateCalendarLink = (
  clientId: Uuid,
  id: Uuid,
): Promise<{ readonly ok: boolean }> =>
  request<{ readonly ok: boolean }>(API_ROUTES.calendarLink(clientId, id), { method: 'DELETE' });

/**
 * Os calendarios da plataforma, para o mapeamento.
 *
 * O token vai no corpo e nao volta em lugar nenhum: o servidor o usa para uma
 * leitura e o descarta. Ele so e persistido, cifrado, quando o mapeamento e salvo.
 */
export const discoverPlatformCalendars = (
  clientId: Uuid,
  payload: PlatformDiscoveryRequest,
): Promise<PlatformDiscovery> =>
  request<PlatformDiscovery>(API_ROUTES.platformCalendars(clientId), {
    method: 'POST',
    body: payload,
  });

/** Instrumento: a paciente reservando do lado de lá. Só em modo simulado. */
export const simulatePlatformBooking = (
  clientId: Uuid,
  id: Uuid,
  payload: {
    readonly date: string;
    readonly hora: string;
    readonly patientName?: string;
    readonly patientPhone?: string;
  },
): Promise<SyncResult> =>
  request<SyncResult>(API_ROUTES.simulateBooking(clientId, id), {
    method: 'POST',
    body: payload,
  });

export const resolveSyncConflict = (clientId: Uuid, id: Uuid): Promise<{ readonly ok: boolean }> =>
  request<{ readonly ok: boolean }>(API_ROUTES.resolveConflict(clientId, id), { method: 'POST' });

export const fetchAgendaDays = (
  clientId: Uuid,
  doctorId: Uuid,
  from: string,
  to: string,
): Promise<readonly AgendaDay[]> =>
  request<readonly AgendaDay[]>(API_ROUTES.agendaDays(clientId, doctorId, from, to));

// ── Marcação a partir da conversa ───────────────────────────────────────────

export const fetchFreeSlots = (
  clientId: Uuid,
  q: {
    /** Obrigatória: são os começos possíveis que dependem dela. */
    durationMin: number;
    from?: string;
    weekdays?: readonly number[];
    periodo?: 'manha' | 'tarde' | 'qualquer';
    limit?: number;
    overbook?: boolean;
  },
): Promise<FreeSlotSearch> => {
  const p = new URLSearchParams();
  p.set('durationMin', String(q.durationMin));
  if (q.from !== undefined && q.from !== '') p.set('from', q.from);
  if (q.weekdays !== undefined && q.weekdays.length > 0) p.set('weekdays', q.weekdays.join(','));
  if (q.periodo !== undefined && q.periodo !== 'qualquer') p.set('periodo', q.periodo);
  if (q.limit !== undefined) p.set('limit', String(q.limit));
  if (q.overbook === true) p.set('overbook', 'true');
  return request<FreeSlotSearch>(`${API_ROUTES.freeSlots(clientId)}?${p.toString()}`);
};

/** Confere uma lista de horarios sem a janela da busca. */
export const checkSlots = (
  clientId: Uuid,
  durationMin: number,
  locationId: Uuid | null,
  slots: readonly { date: string; hora: string }[],
): Promise<SlotCheckResult> =>
  request<SlotCheckResult>(API_ROUTES.checkSlots(clientId), {
    method: 'POST',
    body: { durationMin, locationId, slots },
  });

export const bookAppointments = (payload: BookRequest): Promise<BookResult> =>
  request<BookResult>(API_ROUTES.book, { method: 'POST', body: payload });

export const fetchPatientAgenda = (contactId: Uuid): Promise<PatientAgenda> =>
  request<PatientAgenda>(API_ROUTES.patientAgenda(contactId));

export const cancelAppointment = (
  id: Uuid,
  serie = false,
): Promise<{ readonly ok: boolean; readonly cancelled: number }> =>
  request<{ readonly ok: boolean; readonly cancelled: number }>(
    `${API_ROUTES.cancelAppointment(id)}${serie ? '?serie=true' : ''}`,
    { method: 'DELETE' },
  );

export const rescheduleAppointment = (
  id: Uuid,
  body: RescheduleRequest,
): Promise<{ readonly ok: boolean; readonly overbooked: boolean }> =>
  request(API_ROUTES.rescheduleAppointment(id), { method: 'POST', body });

export const confirmAppointment = (id: Uuid): Promise<{ readonly ok: boolean }> =>
  request(API_ROUTES.confirmAppointment(id), { method: 'POST' });

export const markPresence = (id: Uuid, present: boolean): Promise<{ readonly ok: boolean }> =>
  request(API_ROUTES.appointmentPresence(id), {
    method: 'POST',
    body: { present } satisfies PresenceRequest,
  });

/** A conversa do paciente com este médico, criada vazia quando ainda não existe. */
export const openPatientConversation = (
  clientId: Uuid,
  contactId: Uuid,
): Promise<{ readonly conversationId: Uuid }> =>
  request<{ readonly conversationId: Uuid }>(API_ROUTES.patientConversation(clientId, contactId), {
    method: 'POST',
  });

/** Pacientes por nome ou telefone, de todos os médicos, para iniciar conversa. */
export const searchContacts = (q: string): Promise<readonly ContactHit[]> =>
  request<readonly ContactHit[]>(`${API_ROUTES.contactSearch}?q=${encodeURIComponent(q)}`);

/** Desmarca tudo o que o paciente tem pela frente, numa transacao so. */
export const cancelPatientAgenda = (
  contactId: Uuid,
  clientId?: Uuid,
): Promise<{ readonly ok: boolean; readonly cancelled: number }> =>
  request<{ readonly ok: boolean; readonly cancelled: number }>(
    API_ROUTES.cancelPatientAgenda(contactId, clientId),
    { method: 'DELETE' },
  );

// ── Equipes (docs/domain/equipes.md) ─────────────────────────────────────────

type Ok = { readonly ok: boolean };

export const updatePerfil = (body: PerfilUpdate): Promise<Ok> =>
  request<Ok>(API_ROUTES.perfil, { method: 'PATCH', body });

export const fetchPlantao = (): Promise<MeuPlantao> => request<MeuPlantao>(API_ROUTES.plantao);

export const fetchFocus = (): Promise<InboxFocus> => request<InboxFocus>(API_ROUTES.focus);

export const saveFocus = (body: InboxFocus): Promise<Ok> =>
  request<Ok>(API_ROUTES.focus, { method: 'PUT', body });

export const sendTyping = (conversationId: Uuid): Promise<Ok> =>
  request<Ok>(API_ROUTES.typing(conversationId), { method: 'POST' });

export const fetchTeams = (): Promise<readonly TeamSummary[]> =>
  request<readonly TeamSummary[]>(API_ROUTES.teams);

export const fetchTeam = (id: Uuid): Promise<TeamDetail> =>
  request<TeamDetail>(API_ROUTES.team(id));

export const createTeam = (body: CreateTeamRequest): Promise<{ readonly id: Uuid }> =>
  request<{ readonly id: Uuid }>(API_ROUTES.teams, { method: 'POST', body });

export const renameTeam = (id: Uuid, body: CreateTeamRequest): Promise<Ok> =>
  request<Ok>(API_ROUTES.team(id), { method: 'PATCH', body });

export const archiveTeam = (id: Uuid): Promise<Ok> =>
  request<Ok>(API_ROUTES.teamArchive(id), { method: 'POST' });

export const leaveTeam = (id: Uuid): Promise<Ok> =>
  request<Ok>(API_ROUTES.teamLeave(id), { method: 'POST' });

export const transferLeadership = (id: Uuid, userId: Uuid): Promise<Ok> =>
  request<Ok>(API_ROUTES.teamTransfer(id), { method: 'POST', body: { userId } });

export const inviteToTeam = (id: Uuid, body: TeamInviteRequest): Promise<Ok> =>
  request<Ok>(API_ROUTES.teamInvites(id), { method: 'POST', body });

export const revokeInvite = (id: Uuid, inviteId: Uuid): Promise<Ok> =>
  request<Ok>(API_ROUTES.teamInvite(id, inviteId), { method: 'DELETE' });

export const updateMember = (id: Uuid, userId: Uuid, body: UpdateMemberRequest): Promise<Ok> =>
  request<Ok>(API_ROUTES.teamMember(id, userId), { method: 'PATCH', body });

export const removeMember = (id: Uuid, userId: Uuid): Promise<Ok> =>
  request<Ok>(API_ROUTES.teamMember(id, userId), { method: 'DELETE' });

export const fetchTeamTerm = (): Promise<TeamTerm> => request<TeamTerm>(API_ROUTES.teamTerm);

export const fetchAttestation = (): Promise<AttestationText> =>
  request<AttestationText>(API_ROUTES.attestationText);

export const fetchMyInvites = (): Promise<readonly IncomingInvite[]> =>
  request<readonly IncomingInvite[]>(API_ROUTES.myInvites);

export const acceptInvite = (
  inviteId: Uuid,
  body: AcceptInviteRequest,
): Promise<{ readonly teamId: Uuid }> =>
  request<{ readonly teamId: Uuid }>(API_ROUTES.acceptInvite(inviteId), { method: 'POST', body });

export const declineInvite = (inviteId: Uuid, body: DeclineInviteRequest): Promise<Ok> =>
  request<Ok>(API_ROUTES.declineInvite(inviteId), { method: 'POST', body });

export const shareDoctors = (id: Uuid, body: ShareRequest): Promise<Ok> =>
  request<Ok>(API_ROUTES.teamShares(id), { method: 'POST', body });

export const updateShare = (id: Uuid, clientId: Uuid, body: UpdateShareRequest): Promise<Ok> =>
  request<Ok>(API_ROUTES.teamShare(id, clientId), { method: 'PATCH', body });

export const revokeShare = (id: Uuid, clientId: Uuid): Promise<Ok> =>
  request<Ok>(API_ROUTES.teamShare(id, clientId), { method: 'DELETE' });

export const saveSharePolicy = (id: Uuid, body: SharePolicyRequest): Promise<Ok> =>
  request<Ok>(API_ROUTES.teamSharePolicy(id), { method: 'PUT', body });

export const fetchTeamShifts = (id: Uuid): Promise<TeamShifts> =>
  request<TeamShifts>(API_ROUTES.teamShifts(id));

export const addShiftRule = (id: Uuid, body: ShiftRuleRequest): Promise<{ readonly id: Uuid }> =>
  request<{ readonly id: Uuid }>(API_ROUTES.teamShifts(id), { method: 'POST', body });

export const removeShiftRule = (id: Uuid, shiftId: Uuid): Promise<Ok> =>
  request<Ok>(API_ROUTES.teamShift(id, shiftId), { method: 'DELETE' });

export const addShiftOverride = (
  id: Uuid,
  body: ShiftOverrideRequest,
): Promise<{ readonly id: Uuid }> =>
  request<{ readonly id: Uuid }>(API_ROUTES.teamShiftOverrides(id), { method: 'POST', body });

export const removeShiftOverride = (id: Uuid, overrideId: Uuid): Promise<Ok> =>
  request<Ok>(API_ROUTES.teamShiftOverride(id, overrideId), { method: 'DELETE' });

export const setAutoAssign = (id: Uuid, autoAssign: boolean): Promise<Ok> =>
  request<Ok>(API_ROUTES.teamAutoAssign(id), { method: 'PUT', body: { autoAssign } });

export const fetchTeamActivity = (id: Uuid): Promise<readonly ActivityEntry[]> =>
  request<readonly ActivityEntry[]>(API_ROUTES.teamActivity(id));

export const fetchClientActivity = (id: Uuid): Promise<readonly ActivityEntry[]> =>
  request<readonly ActivityEntry[]>(API_ROUTES.clientActivity(id));

export const assignConversation = (
  id: Uuid,
  body: AssignRequest,
): Promise<{ readonly ok: boolean; readonly assigneeId: Uuid | null }> =>
  request(API_ROUTES.assignConversation(id), { method: 'POST', body });
